import type { ReactNode } from "react";

import type { Branding } from "@lagoon/config/branding";
import { Card, CardTitle, H3, Mono, PageHeader, Text } from "@lagoon/ui";

/**
 * How the product works, for the person using it.
 *
 * Written against the failure modes that actually happened during the build
 * rather than a feature list: an archive with no `index.html` at the top level
 * (which presented as "there is no site at this address"), the two-step deploy
 * surprising people who expected uploading to publish, and nobody knowing that
 * removing someone from the list takes effect immediately.
 */
export function Help(props: { sitesDomain: string; branding: Branding }): React.ReactElement {
  return (
    <div className="lg:flex lg:max-w-4xl lg:flex-col lg:gap-7">
      <PageHeader
        title={`How ${props.branding.productName} works`}
        description="Private websites: a site is invisible until you say who may read it."
      />

      <Section title="Putting a site up">
        <Text size="sm" tone="muted">
          Deploying and publishing are two steps on purpose. Uploading a file changes nothing that
          visitors see; you decide when it goes live.
        </Text>
        <ol className="lg:flex lg:list-decimal lg:flex-col lg:gap-3 lg:pl-5 lg:text-body-sm lg:text-muted-foreground lg:marker:font-mono lg:marker:text-subtle">
          <li>
            <strong className="lg:text-strong">Zip your site.</strong> The <Mono>index.html</Mono>{" "}
            must be at the <em>top level of the archive</em>, not inside a folder. If you zip a
            folder called <Mono>my-site</Mono>, the file lands at <Mono>my-site/index.html</Mono>{" "}
            and the site will answer &ldquo;there is no page here&rdquo;. Select the files and zip
            those, not the folder around them.
          </li>
          <li>
            <strong className="lg:text-strong">Upload it</strong> on the site&rsquo;s Deploy tab. It
            goes straight to storage, so large sites are fine, but the upload link is good for 15
            minutes.
          </li>
          <li>
            <strong className="lg:text-strong">Look at it first.</strong> Open the release and
            choose <em>Preview &amp; changes</em>. The preview link is private and behind the same
            list as the site, so only people who could already read the site can open it.
          </li>
          <li>
            <strong className="lg:text-strong">Publish.</strong> Every visitor sees it within
            seconds.
          </li>
        </ol>
        <Text size="caption" tone="subtle">
          Something wrong with what you published? Roll back to any earlier release. It takes effect
          just as fast, and you will be asked for a reason so the trail says why.
        </Text>
      </Section>

      <Section title="Who can read it">
        <Text size="sm" tone="muted">
          Nobody, until you add them. There are four ways in, for four different situations.
        </Text>

        <div className="lg:flex lg:flex-col lg:gap-2">
          <H3>The list, for people who should keep access</H3>
          <Text size="sm" tone="muted">
            Add an email address. When that person opens the site they are emailed a six-digit code,
            type it in, and they are through. No account, no password, nothing for them to set up.
          </Text>
          <Text size="sm" tone="muted">
            <strong className="lg:text-strong">
              Removing someone takes effect on their very next click
            </strong>
            , not whenever they happen to sign out. If they are reading a page right now, their next
            one asks them to sign in again.
          </Text>
        </div>

        <div className="lg:flex lg:flex-col lg:gap-2">
          <H3>A whole domain, for a whole organisation</H3>
          <Text size="sm" tone="muted">
            Allow a domain and anyone with an email address there can sign in, each proving their
            own address with a code. One rule instead of forty entries, and no one to forget.
            Removing the domain cuts everyone it admitted off on their next click, except people you
            also listed individually.
          </Text>
        </div>

        <div className="lg:flex lg:flex-col lg:gap-2">
          <H3>A group, for the same people across many sites</H3>
          <Text size="sm" tone="muted">
            A group is one list of people, curated on the Groups page and attached to any of your
            sites. Show every board pack to the same five people by attaching the same group; when
            someone joins or leaves, change the group once and every attached site follows
            immediately.
          </Text>
        </div>

        <div className="lg:flex lg:flex-col lg:gap-2">
          <H3>A guest link, for showing somebody once</H3>
          <Text size="sm" tone="muted">
            A link that works until it expires. Good for a client review or a board pack: they need
            no account and never join the list. Choose how long it lasts, and optionally how many
            times it can be used.
          </Text>
          <Text size="sm" tone="muted">
            The link is shown once, when you create it. Revoking it cuts off anyone using it
            immediately, not when it would have expired.
          </Text>
        </div>
      </Section>

      <Section title="Knowing who looked">
        <Text size="sm" tone="muted">
          The Activity tab lists every sign-in: who, when, and how they got in. One row is one
          sign-in, not one page view, so somebody who reads twenty pages appears once.
        </Text>
        <Text size="sm" tone="muted">
          If somebody you removed keeps trying to get back in, their row says so with a count. That
          is usually worth a look.
        </Text>
        <Text size="caption" tone="subtle">
          Sign-ins are kept for 90 days. You can end a single session without removing the person,
          which is what you want when a laptop goes missing.
        </Text>
      </Section>

      <Section title="Being told what happened">
        <Text size="sm" tone="muted">
          By default we email you when a deploy finishes, including when it fails and why. You can
          turn that off, or add a webhook if you would rather your own systems heard about it. Each
          webhook is signed so your endpoint can prove the message came from us.
        </Text>
      </Section>

      <Section title="Doing all this from Claude">
        <Text size="sm" tone="muted">
          Everything here can be done by asking Claude, through the {props.branding.productName}{" "}
          connector: create a site, deploy, check what a release would change, publish, roll back,
          manage the list, and hand out guest links. Ask it &ldquo;what would change if I published
          this?&rdquo; before you do.
        </Text>
      </Section>

      <Section title="When something looks wrong">
        <dl className="lg:flex lg:flex-col lg:gap-4">
          <Faq question="The site says &ldquo;there is no page here&rdquo;">
            The archive has no <Mono>index.html</Mono> at its top level. Re-zip the files themselves
            rather than the folder containing them.
          </Faq>
          <Faq question="A release is stuck on &ldquo;waiting for upload&rdquo;">
            The upload never finished, usually a closed tab or a link that timed out after 15
            minutes. Nothing is broken: deploy the file again.
          </Faq>
          <Faq question="Someone says their code does not work">
            Codes last ten minutes and asking for a new one replaces the old one, so an older email
            will not work. Check the address on the list matches theirs exactly.
          </Faq>
          <Faq question="Someone was never emailed a code">
            They are almost certainly not on the list, or are on it under a different address. For
            safety the site says the same thing either way, so it cannot be used to discover who has
            access.
          </Faq>
        </dl>
      </Section>

      <Text size="sm" tone="subtle">
        Sites live at <Mono>your-site.{props.sitesDomain}</Mono>.
      </Text>
    </div>
  );
}

/** One card of prose. The heading is the card title, at heading level two. */
function Section(props: { title: string; children: ReactNode }): React.ReactElement {
  return (
    <Card as="section" interactive={false}>
      <div className="lg:flex lg:flex-col lg:gap-4">
        <CardTitle as="h2">{props.title}</CardTitle>
        {props.children}
      </div>
    </Card>
  );
}

/** A question and its answer, as the definition pair they are. */
function Faq(props: { question: string; children: ReactNode }): React.ReactElement {
  return (
    <div>
      <dt className="lg:text-body-sm lg:font-bold lg:text-strong">{props.question}</dt>
      <dd className="lg:mt-1 lg:text-body-sm lg:text-muted-foreground">{props.children}</dd>
    </div>
  );
}
